import React, { useEffect, useRef, useState } from 'react';
import { auth } from '../../Api';
import { formatPhone, localPart } from '../../utils/phone';

// Suggestions are asked for from this many digits typed (the server sends at most 10), a moment after the last key.
const SUGGEST_FROM_DIGITS = 3;
const SUGGEST_DELAY_MS = 250;

// One line of the list: the number as the box shows it, then who it is on, its first owner (who has had it
// longest) first: "712 345678: Guard Jo Smith (also used by Supervisor Ann Lee)" (the label is the server's,
// App\PersonPhones::suggestions).
export function suggestionText(suggestion, dialCode) {
	return (dialCode ? localPart(suggestion.phone, dialCode) : formatPhone(suggestion.phone))
		+ (suggestion.label ? ': ' + suggestion.label : '');
}

// A person's phone field: the company's dialling code as a fixed label ("+254"), then the local number.
// value is what is stored or typed: a stored "+254712345678" is shown as "712 345678", a foreign
// "+44..." number in full. onChange gets the text as typed; utils/phone toInternational() completes it.
// With suggest, from the 3rd digit typed (leading 0s not counted: "0712" finds what "712" finds) a list
// under the box offers the numbers already used in the company open in the admin website (anyone's, as
// suggestionText), narrowing with each digit; clicking one fills the
// box with it. Saving checks it as any typed number. editing ({ kind: 'guard', id }) is the person whose form
// this is: their own numbers are left out of the list.
export default function LocalPhoneField({ dialCode, value, onChange, required, suggest, editing }) {
	const [suggestions, setSuggestions] = useState([]);
	const [open, setOpen] = useState(false);
	const [active, setActive] = useState(-1);
	const timer = useRef(null);
	// Only the reply to the latest text typed is shown
	const asked = useRef(0);

	useEffect(() => () => {
		clearTimeout(timer.current);
		asked.current++;
	}, []);

	const lookUp = (typed) => {
		clearTimeout(timer.current);
		const ask = ++asked.current;
		if (String(typed).replace(/\D/g, '').replace(/^0+/, '').length < SUGGEST_FROM_DIGITS) {
			setSuggestions([]);
			setOpen(false);
			return;
		}
		timer.current = setTimeout(() => {
			const request = { company_id: localStorage.getItem('companyId'), phone: typed };
			if (editing && editing.kind && editing.id != null && String(editing.id) !== 'null') {
				request.except_kind = editing.kind;
				request.except_id = editing.id;
			}
			auth.getPhoneSuggestions(request)
				.then((res) => {
					if (ask !== asked.current) {
						return;
					}
					const list = Number(res.data.code) === 200 && Array.isArray(res.data.suggestions) ? res.data.suggestions : [];
					setSuggestions(list);
					setActive(-1);
					setOpen(list.length > 0);
				})
				.catch(() => {
					if (ask === asked.current) {
						setSuggestions([]);
						setOpen(false);
					}
				});
		}, SUGGEST_DELAY_MS);
	};

	const typed = (text) => {
		onChange(text);
		if (suggest) {
			lookUp(text);
		}
	};

	const pick = (suggestion) => {
		clearTimeout(timer.current);
		asked.current++;
		setOpen(false);
		setSuggestions([]);
		onChange(suggestion.phone);
	};

	const keyDown = (event) => {
		if (!open || !suggestions.length) {
			return;
		}
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			setActive((active + 1) % suggestions.length);
		} else if (event.key === 'ArrowUp') {
			event.preventDefault();
			setActive(active <= 0 ? suggestions.length - 1 : active - 1);
		} else if (event.key === 'Enter' && active >= 0) {
			// Picks the highlighted number instead of saving the form
			event.preventDefault();
			pick(suggestions[active]);
		} else if (event.key === 'Escape') {
			setOpen(false);
		}
	};

	return (
		<div style={{ position: 'relative', width: '100%' }}>
			<div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
				<span
					className="phone-dial-code"
					title={dialCode ? "The company's dialling code (from its country in the company details)" : 'The company has no country in its company details: type the full number with +'}
					style={{ padding: '0 8px', fontWeight: 600, whiteSpace: 'nowrap' }}>
					{dialCode ? '+' + dialCode : '+?'}
				</span>
				<input
					className="location-text-input"
					type="tel"
					inputMode="tel"
					placeholder={dialCode ? 'Local number, e.g. 712 345 678' : 'Full number with +'}
					value={localPart(value, dialCode)}
					onChange={(event) => typed(event.target.value)}
					onKeyDown={keyDown}
					onBlur={() => setOpen(false)}
					onFocus={() => setOpen(suggestions.length > 0)}
					autoComplete="off"
					required={required}
					style={{ flex: 1 }} />
			</div>
			{suggest && open && suggestions.length ? (
				<ul
					role="listbox"
					title="Numbers already used in this company"
					style={{
						position: 'absolute', zIndex: 1000, left: 0, right: 0, top: '100%', margin: 0, padding: 0,
						listStyle: 'none', background: '#fff', border: '1px solid #ced4da', borderRadius: 4,
						boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)', maxHeight: 320, overflowY: 'auto',
					}}>
					{suggestions.map((suggestion, index) => (
						<li
							key={suggestion.phone}
							role="option"
							aria-selected={index === active}
							// mousedown, before the box loses focus and closes the list
							onMouseDown={(event) => {
								event.preventDefault();
								pick(suggestion);
							}}
							onMouseEnter={() => setActive(index)}
							style={{
								padding: '6px 10px', cursor: 'pointer', color: '#212529',
								background: index === active ? '#e9ecef' : '#fff',
							}}>
							{suggestionText(suggestion, dialCode)}
						</li>
					))}
				</ul>
			) : null}
		</div>
	);
}
